import React, { useState, useEffect } from 'react';
import { Wallet } from 'lucide-react';
import Dropdown from '../components/Dropdown';
import CTable from '../components/shared/c-table';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import usePersistedState from '../hooks/usePersistedState';
import { companyToday } from '../utils/dateUtils';

const LeaveBalances = () => {
  const currentYear = Number(companyToday().slice(0, 4));
  const [year, setYear] = usePersistedState('leaveBalances.year', currentYear);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/api/leaves/balances/me', { params: { year } })
      .then(res => setBalances(res.data.data || []))
      .catch(() => toast.error('Failed to load balances'))
      .finally(() => setLoading(false));
  }, [year]);

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Remaining and Usage are derived, so they render rather than map to a field.
  const columns = [
    { key: 'leave_type_name', header: 'Leave Type', cellClassName: 'font-medium' },
    { key: 'total_quota', header: 'Quota', cellClassName: 'text-gray-700' },
    { key: 'used_days', header: 'Used', cellClassName: 'text-gray-700' },
    {
      key: 'remaining',
      header: 'Remaining',
      cellClassName: 'font-medium',
      render: (b) => Math.max(0, b.total_quota - b.used_days),
    },
    {
      key: 'usage',
      header: 'Usage',
      cellClassName: 'w-40',
      render: (b) => {
        const pct = b.total_quota > 0
          ? Math.min(100, Math.round((b.used_days / b.total_quota) * 100))
          : 0;
        const barColor = pct >= 90 ? 'bg-danger-500' : pct >= 70 ? 'bg-warning-500' : 'bg-success-500';
        return (
          <div className="flex items-center space-x-2">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div className={`h-2 rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs text-gray-500 w-9 text-right">{pct}%</span>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Wallet className="h-7 w-7 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">My Leave Balances</h1>
        </div>
        <Dropdown
          data-testid="leave-balances-year-filter"
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="w-auto"
        >
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </Dropdown>
      </div>

      <CTable
        columns={columns}
        data={balances}
        loading={loading}
        rowKey="id"
        getRowProps={(b) => ({ 'data-testid': `leave-balances-row-${b.id}` })}
        emptyMessage={
          <>
            <Wallet className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm">No balance records for {year}</p>
          </>
        }
      />
    </div>
  );
};

export default LeaveBalances;
